// MARK://dhk8/admin-shell/pages/modules/UsersPage
// Módulo 3 — Personal / Roles / PINs del POS → dhk8-platform-auth (`/api/users` + `/v1/users/:id/pin`).
// Lista del equipo (Nombre / Correo / Rol / ID de POS / Estado), filtro por rol y alta de
// personal con PIN-reveal de una sola vez. Cada fila ABRE la ficha del usuario
// (/users/:id) donde se edita (nombre/rol/estado), se reinicia el PIN, se suspende/activa
// y se da de baja. "Agregar personal" se queda como sheet aquí (crear). Funciona en test o
// live (manda el x-dhk8-tenant-id activo).
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Copy, ChevronRight } from 'lucide-react'
import * as users from '../../lib/clients/users.js'
import type { StaffRole, StaffStatus } from '../../lib/clients/users.js'
import * as rolesClient from '../../lib/clients/roles.js'
import type { Role } from '../../lib/clients/roles.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import Sheet from '../../components/Sheet.js'

const STATUS_LABEL: Record<StaffStatus, string> = { ACTIVE: 'Activo', SUSPENDED: 'Suspendido', DELETED: 'Eliminado' }

// Etiqueta amistosa de un rol para el dropdown de asignación: los built-ins
// conservan su nombre es-MX (STAFF_ROLE_LABEL); los custom usan su descripción o
// el nombre Title-cased.
function roleOptionLabel(r: Role): string {
  const builtin = users.STAFF_ROLE_LABEL[r.name as StaffRole]
  if (builtin) return builtin
  if (r.description) return r.description
  return r.name.charAt(0).toUpperCase() + r.name.slice(1)
}

export default function UsersPage() {
  const navigate = useNavigate()
  const [roleFilter, setRoleFilter] = useState<StaffRole | ''>('')
  const list = useAsync((s) => users.listUsers(roleFilter || undefined, s), [roleFilter])
  // Roles asignables: built-in + custom, en vivo (#3 F4). El <select> de alta se alimenta
  // de aquí para poder asignar roles a la medida; si aún no carga, caemos a los built-in
  // para no bloquear la pantalla.
  const rolesList = useAsync((s) => rolesClient.listRoles(s), [])
  const assignableRoles: { name: string; label: string }[] =
    rolesList.data?.map((r) => ({ name: r.name, label: roleOptionLabel(r) }))
    ?? users.STAFF_ROLES.map((r) => ({ name: r, label: users.STAFF_ROLE_LABEL[r] }))

  // create
  const [createOpen, setCreateOpen] = useState(false)
  const [cForm, setCForm] = useState<{ fullName: string; email: string; role: StaffRole; pinId: string; pin: string }>({ fullName: '', email: '', role: 'waiter', pinId: '', pin: '' })
  const [busy, setBusy] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [revealedPin, setRevealedPin] = useState<{ name: string; pinId: string | null; pin: string | null } | null>(null)

  function openCreate() { setCForm({ fullName: '', email: '', role: 'waiter', pinId: '', pin: '' }); setCreateError(null); setCreateOpen(true) }
  async function doCreate() {
    setBusy(true); setCreateError(null)
    try {
      const u = await users.createUser({ fullName: cForm.fullName.trim(), email: cForm.email.trim() || null, role: cForm.role, ...(cForm.pinId ? { pinId: cForm.pinId } : {}), ...(cForm.pin ? { pin: cForm.pin } : {}) })
      setCreateOpen(false)
      if (u.pin || u.pinId) setRevealedPin({ name: u.fullName, pinId: u.pinId, pin: u.pin ?? null })
      list.reload()
    } catch (e) { setCreateError(e instanceof Error ? e.message : String(e)) } finally { setBusy(false) }
  }
  function copy(text: string) { void navigator.clipboard?.writeText(text) }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div><h1 className="adm-page__title">Personal</h1><p className="adm-page__sub">Equipo del negocio, roles y PINs de acceso al POS</p></div>
        <div className="adm-page__actions">
          <select className="adm-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as StaffRole | '')} aria-label="Filtrar por rol">
            <option value="">Todos los roles</option>
            {users.STAFF_ROLES.map((r) => <option key={r} value={r}>{users.STAFF_ROLE_LABEL[r]}</option>)}
          </select>
          <button className="adm-btn adm-btn--primary" onClick={openCreate}><Plus size={16} /> Agregar personal</button>
        </div>
      </div>
      {asyncState(list.loading, list.error, list.reload)}
      {list.data && <div className="adm-card adm-card--pad0"><div className="adm-list">
        {list.data.length === 0 && <div className="adm-empty">Sin personal</div>}
        {list.data.map((u) => (
          <button key={u.id} type="button" className="adm-row" onClick={() => navigate(`/users/${u.id}`)}>
            <div className="adm-row__main">
              <div className="adm-row__title">{u.fullName}</div>
              <div className="adm-row__meta">{u.email || 'sin correo'} · {users.STAFF_ROLE_LABEL[u.role as StaffRole] ?? u.role}{u.pinId ? ` · ID de POS ${u.pinId}` : ' · sin ID de POS'}</div>
            </div>
            <div className="adm-row__tail">
              <span className={`adm-badge ${u.status === 'ACTIVE' ? 'adm-badge--ok' : 'adm-badge--danger'}`}>{STATUS_LABEL[u.status]}</span>
              <ChevronRight size={16} className="adm-muted" aria-hidden />
            </div>
          </button>
        ))}
      </div></div>}

      {/* Crear personal */}
      <Sheet open={createOpen} title="Agregar personal" onClose={() => setCreateOpen(false)}
        footer={<><button className="adm-btn" onClick={() => setCreateOpen(false)}>Cancelar</button><button className="adm-btn adm-btn--primary" onClick={doCreate} disabled={busy || !cForm.fullName.trim() || (!!cForm.pinId && cForm.pinId.length !== 6) || (!!cForm.pin && cForm.pin.length !== 6)}>Agregar</button></>}>
        <label className="adm-field"><span className="adm-field__label">Nombre completo</span><input className="adm-input" value={cForm.fullName} onChange={(e) => setCForm({ ...cForm, fullName: e.target.value })} /></label>
        <label className="adm-field"><span className="adm-field__label">Correo (opcional)</span><input className="adm-input" type="email" value={cForm.email} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} placeholder="solo si entrará al admin" /></label>
        <label className="adm-field"><span className="adm-field__label">Rol</span><select className="adm-select" value={cForm.role} onChange={(e) => setCForm({ ...cForm, role: e.target.value as StaffRole })}>{assignableRoles.map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}</select></label>
        <label className="adm-field"><span className="adm-field__label">ID de POS (6 dígitos, opcional)</span><input className="adm-input" inputMode="numeric" maxLength={6} value={cForm.pinId} onChange={(e) => setCForm({ ...cForm, pinId: e.target.value.replace(/\D/g, '') })} placeholder="se genera solo si lo dejas vacío" /></label>
        <label className="adm-field"><span className="adm-field__label">PIN (6 dígitos, opcional)</span><input className="adm-input" inputMode="numeric" maxLength={6} value={cForm.pin} onChange={(e) => setCForm({ ...cForm, pin: e.target.value.replace(/\D/g, '') })} placeholder="se genera solo si lo dejas vacío" /></label>
        {createError && <div className="adm-banner adm-banner--danger" role="alert">{createError}</div>}
      </Sheet>

      {/* PIN revelado una vez */}
      <Sheet open={revealedPin != null} title="Credencial de POS" onClose={() => setRevealedPin(null)}
        footer={<button className="adm-btn adm-btn--primary" onClick={() => setRevealedPin(null)}>Entendido</button>}>
        <p className="adm-row__meta" style={{ marginBottom: 12 }}>Anota esto ahora — el PIN no se vuelve a mostrar. {revealedPin?.name} lo usará para entrar al POS.</p>
        <div className="adm-banner" style={{ background: 'var(--pos-info-bg)', color: 'var(--pos-info)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {revealedPin?.pinId && <span><strong>ID de POS:</strong> <code>{revealedPin.pinId}</code></span>}
          {revealedPin?.pin && <span><strong>PIN:</strong> <code>{revealedPin.pin}</code></span>}
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => copy(`ID de POS: ${revealedPin?.pinId ?? '—'} · PIN: ${revealedPin?.pin ?? '—'}`)}><Copy size={14} /> Copiar</button>
        </div>
      </Sheet>
    </div>
  )
}
